import { useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteConfig, categories } from '@/mocks/homeData';
import { categoryArticles, getArticlesByCategory, getCategoryInfo } from '@/mocks/articleData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = decodeURIComponent(slug || '全部');
  const articles = getArticlesByCategory(category);
  const info = getCategoryInfo(category);
  const breadcrumbName = category === '全部' ? '全部文章' : category;

  const breadcrumbSchema = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: siteConfig.url,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: breadcrumbName,
            item: `${siteConfig.url}/category/${encodeURIComponent(category)}`,
          },
        ],
      }),
    [breadcrumbName, category],
  );

  const webpageSchema = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${info.name} - ${siteConfig.name}`,
        description: info.description,
        url: `${siteConfig.url}/category/${encodeURIComponent(category)}`,
        isPartOf: {
          '@type': 'WebSite',
          name: siteConfig.name,
          url: siteConfig.url,
        },
      }),
    [category, info],
  );

  useEffect(() => {
    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.textContent = breadcrumbSchema;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.textContent = webpageSchema;
    document.head.appendChild(script2);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, [breadcrumbSchema, webpageSchema]);

  return (
    <>
      <Navbar />
      <main className="pt-20 md:pt-24">
        <section className="py-10 md:py-16 px-4 md:px-6">
          <div className="max-w-screen-2xl mx-auto">
            <nav aria-label="面包屑导航" className="mb-6">
              <ol className="flex items-center gap-2 text-sm text-foreground-500">
                <li>
                  <Link to="/" className="hover:text-foreground-800 transition-colors duration-200">
                    首页
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
                <li className="text-foreground-800 font-medium">{breadcrumbName}</li>
              </ol>
            </nav>

            <div className="mb-8 md:mb-10">
              <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-tight mb-3">
                {info.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl leading-relaxed">
                {info.description}
              </p>
              <span className="inline-block mt-3 px-3 py-1 rounded-full bg-accent-100/80 text-accent-800 text-xs font-label">
                共 {info.count} 篇
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.length > 0 ? (
                articles.map((article) => (
                  <Link
                    key={article.id}
                    to="/"
                    className="group cursor-pointer"
                  >
                    <div className="rounded-lg overflow-hidden border border-foreground-200/40 bg-background-50 transition-all duration-300 group-hover:border-accent-300/60">
                      <div className="relative h-[220px] md:h-[260px] overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-background-50/90 text-xs font-label text-foreground-700">
                          {article.category}
                        </span>
                      </div>
                      <div className="p-4 md:p-5">
                        <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-accent-600 transition-colors duration-200">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 mb-4 line-clamp-2 leading-relaxed">
                          {article.summary}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <span>{article.date}</span>
                          <span>
                            <i className="ri-eye-line mr-1"></i>
                            {(article.views / 10000).toFixed(1)}万
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="col-span-full text-center py-16">
                  <p className="text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100/50">
          <div className="max-w-screen-2xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              其他分类
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to="/"
                  className={`group cursor-pointer rounded-lg border p-4 text-center transition-all duration-300 ${
                    cat.name === category
                      ? 'border-accent-400 bg-accent-50/60'
                      : 'border-foreground-200/40 bg-background-50 hover:border-accent-300/60'
                  }`}
                >
                  <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center transition-colors duration-300 ${
                    cat.name === category ? 'bg-accent-200/70' : 'bg-secondary-100 group-hover:bg-accent-100/60'
                  }`}>
                    <i className={`${cat.icon} text-lg ${cat.name === category ? 'text-accent-700' : 'text-secondary-600 group-hover:text-accent-600'}`}></i>
                  </div>
                  <span className={`text-sm font-label font-medium ${cat.name === category ? 'text-accent-700' : 'text-foreground-700'}`}>
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}